import React, { useEffect, useState, useRef } from 'react';
/**
 * EstimationSliderRenderer — Slider-based estimation input.
 *
 * Config options via question.data:
 *   showValue (boolean, default true) — When false, hides the numeric value
 *     while the student is dragging. Shows "?" instead, forcing genuine estimation
 *     by position on the number line. The value is revealed after submission.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
export function EstimationSliderRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const min = config.min || 0;
  const max = config.max || 100;
  const stepSize = config.step || 1;
  const unit = config.unit || '';
  const showNumberLine = config.showNumberLine !== false;
  const showValue = config.showValue !== false;
  const [value, setValue] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setValue(null);
    setSubmitted(false);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(value !== null);
  }, [value, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      setSubmitted(true);
      onAnswer(value ?? 0);
    }
  }, [shouldValidate, value, onAnswer]);
  const percentage = value !== null ? (value - min) / (max - min) * 100 : 50;
  // Determine what to display in the value badge
  const displayValue = showValue || submitted ? value !== null ? value : '?' : value !== null ? '?' : '?';
  return <div className="w-full max-w-2xl mx-auto p-6 sm:p-8 bg-white rounded-3xl border-2 border-slate-200 shadow-xl" data-id="element-8037">
      <div className="text-center mb-12" data-id="element-8038">
        <h3 className="text-xl font-bold text-slate-700 mb-2" data-id="element-8039">
          {!showValue && !submitted ? 'Schat de waarde op de lijn' : 'Schat de waarde'}
        </h3>
        <div className="inline-flex items-center justify-center min-w-[120px] h-16 px-6 bg-indigo-50 rounded-2xl border-2 border-indigo-100" data-id="element-8040">
          <span className="text-3xl font-black text-indigo-600" data-id="element-8041">
            {displayValue}
          </span>
          {unit && (showValue || submitted) && value !== null && <span className="text-lg font-bold text-indigo-400 ml-2" data-id="element-8042">
              {unit}
            </span>}
        </div>
        {!showValue && !submitted && value !== null && <p className="text-sm text-slate-400 mt-2 font-medium" data-id="element-8043">
            Gebruik de positie op de lijn om te schatten
          </p>}
      </div>

      <div className="relative pt-8 pb-12 px-4" data-id="element-8044">
        {showNumberLine && <div className="absolute top-0 left-0 right-0 flex justify-between px-4 text-sm font-bold text-slate-400" data-id="element-8045">
            <span data-id="element-8046">
              {min} {unit}
            </span>
            <span data-id="element-8047">
              {max} {unit}
            </span>
          </div>}

        <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden" data-id="element-8048">
          <div className="absolute top-0 left-0 bottom-0 bg-indigo-500 transition-all duration-150 ease-out" style={{
          width: `${percentage}%`
        }} data-id="element-8049" />
        </div>

        <input type="range" min={min} max={max} step={stepSize} value={value ?? (min + max) / 2} onChange={e => setValue(Number(e.target.value))} className="absolute top-8 left-0 w-full h-4 opacity-0 cursor-pointer z-10" aria-label="Schatting slider" data-id="element-8050" />

        <motion.div className="absolute top-6 w-8 h-8 bg-white border-4 border-indigo-600 rounded-full shadow-lg pointer-events-none z-0" style={{
        left: `calc(${percentage}% - 16px)`
      }} animate={{
        scale: value !== null ? 1.2 : 1,
        boxShadow: value !== null ? '0 10px 25px -5px rgba(79, 70, 229, 0.4)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20
      }} data-id="element-8051" />

        <div className="absolute top-12 left-0 right-0 flex justify-between px-4 pointer-events-none" data-id="element-8052">
          {[...Array(11)].map((_, i) => <div key={i} className={`w-0.5 bg-slate-200 ${i === 0 || i === 5 || i === 10 ? 'h-4' : 'h-2'}`} data-id="element-8053" />)}
        </div>
      </div>

      <p className="text-center text-slate-500 text-sm font-medium mt-4" data-id="element-8054">
        {!showValue && !submitted ? 'Sleep de knop — het getal zie je pas na het inleveren' : 'Sleep de knop naar de juiste plek op de lijn'}
      </p>
    </div>;
}